<template>
  <div class="fuel-upload">
    <div class="nav-bar-operations">
      <img
        src="@/assets/logogruas.png"
        alt="HomePage"
        class="logo-button"
        @click="$router.push({ path: '/combustibles' })"
      />
    </div>

    <div class="shell">
      <header class="header">
        <div>
          <p class="eyebrow">Combustibles</p>
          <h1>Subir documentos</h1>
          <p class="sub">Carga facturas de proveedores y reportes de consumo</p>
        </div>
      </header>

      <!-- Section tabs -->
      <nav class="tabs" role="tablist">
        <button
          :class="['tab', { 'tab--active': section === 'invoices' }]"
          role="tab"
          @click="section = 'invoices'"
        >
          Facturas
        </button>
        <button
          :class="['tab', { 'tab--active': section === 'reports' }]"
          role="tab"
          @click="section = 'reports'"
        >
          Reportes de cargas
        </button>
      </nav>

      <!-- ============================= Invoices ============================= -->
      <section v-if="section === 'invoices'" class="panel">
        <div class="panel__head">
          <div>
            <h2>Facturas de combustible</h2>
            <p class="muted">Sube el documento y el sistema detectará los campos automáticamente</p>
          </div>
          <button class="btn btn--primary" @click="openInvoiceModal">+ Subir factura</button>
        </div>

        <div v-if="loadingInvoices" class="empty muted">Cargando facturas…</div>
        <div v-else-if="!invoices.length" class="empty muted">Aún no hay facturas registradas.</div>
        <table v-else class="table">
          <thead>
            <tr>
              <th>Proveedor</th>
              <th>No. factura</th>
              <th>Fecha</th>
              <th>Periodo</th>
              <th class="num">Litros</th>
              <th class="num">Importe</th>
              <th>Subida por</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="inv in invoices" :key="inv.id">
              <td>{{ inv.provider_name }}</td>
              <td>{{ inv.invoice_number }}</td>
              <td>{{ inv.invoice_date || '—' }}</td>
              <td>
                <span v-if="inv.period_start || inv.period_end">
                  {{ inv.period_start || '?' }} → {{ inv.period_end || '?' }}
                </span>
                <span v-else>—</span>
              </td>
              <td class="num">{{ formatNumber(inv.total_liters) }}</td>
              <td class="num">{{ formatMoney(inv.total_amount) }}</td>
              <td>{{ inv.created_by || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- ============================= Reports ============================= -->
      <section v-else class="panel">
        <div class="panel__head">
          <div>
            <h2>Reportes de cargas</h2>
            <p class="muted">
              Selecciona el proveedor, sube su reporte y se creará una carga por cada fila
            </p>
          </div>
        </div>

        <div class="report-controls">
          <label class="field">
            <span>Proveedor</span>
            <select v-model="report.providerId" @change="resetReportPreview">
              <option :value="null" disabled>Selecciona un proveedor</option>
              <option v-for="p in providers" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </label>

          <template v-if="reportProvider">
            <div v-if="!reportProvider.has_report_parser" class="warning-box">
              El proveedor <strong>{{ reportProvider.name }}</strong> no tiene un formato de
              reporte configurado. Las cargas de este proveedor deben capturarse manualmente.
            </div>
            <template v-else>
              <label class="field">
                <span>Ligar a factura (opcional)</span>
                <select v-model="report.invoiceId">
                  <option :value="null">Sin factura</option>
                  <option v-for="inv in report.providerInvoices" :key="inv.id" :value="inv.id">
                    {{ inv.invoice_number }}{{ inv.invoice_date ? ` · ${inv.invoice_date}` : '' }}{{ inv.total_amount ? ` · ${formatMoney(inv.total_amount)}` : '' }}
                  </option>
                </select>
              </label>
              <label class="field">
                <span>Archivo del reporte</span>
                <input
                  ref="reportFile"
                  type="file"
                  accept=".csv,.xls,.xlsx,.xlsm,.txt"
                  @change="onReportFileChange"
                />
              </label>
              <button
                class="btn btn--primary"
                :disabled="!report.file || report.parsing"
                @click="parseReport"
              >
                {{ report.parsing ? 'Analizando…' : 'Analizar reporte' }}
              </button>
            </template>
          </template>
        </div>

        <div v-if="report.warnings.length" class="warning-box">
          <p v-for="(w, i) in report.warnings" :key="i">⚠ {{ w }}</p>
        </div>

        <template v-if="report.rows.length">
          <p class="muted">Revisa y corrige los datos antes de guardar; todos los campos son editables.</p>
          <div :class="['table-scroll', { 'table-scroll--picker-open': picker.index >= 0 }]">
            <table class="table table--edit">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Hora</th>
                  <th>Unidad</th>
                  <th>Operador</th>
                  <th>Tarjeta</th>
                  <th>Ticket</th>
                  <th>Producto</th>
                  <th class="num">Odómetro</th>
                  <th class="num">Litros</th>
                  <th class="num">Costo total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in report.rows" :key="i">
                  <td><input v-model="row.date" type="date" class="cell-input cell-input--date" /></td>
                  <td><input v-model="row.time" type="time" step="1" class="cell-input cell-input--time" /></td>
                  <td class="picker-cell">
                    <input
                      v-model="row.unit_search"
                      type="text"
                      autocomplete="off"
                      placeholder="Buscar unidad…"
                      :class="['cell-input', 'cell-input--picker', { 'cell-input--warn': !row.unit_id }]"
                      :title="row.unit_id ? '' : `Unidad del reporte: ${row.unit_eco || row.unit_plates || '—'} (sin asignar)`"
                      @focus="openPicker(i, 'unit')"
                      @input="onPickerInput(i, row, 'unit')"
                      @blur="closePickerSoon"
                    />
                    <ul
                      v-if="picker.index === i && picker.kind === 'unit' && pickerSuggestions.length"
                      class="suggestions"
                    >
                      <li v-for="u in pickerSuggestions" :key="u.id" @mousedown.prevent="selectUnit(row, u)">
                        {{ u.label }}{{ u.plates ? ` · ${u.plates}` : '' }}
                      </li>
                    </ul>
                  </td>
                  <td class="picker-cell">
                    <input
                      v-model="row.operator_search"
                      type="text"
                      autocomplete="off"
                      placeholder="Buscar operador…"
                      :class="['cell-input', 'cell-input--picker', { 'cell-input--warn': !row.operator_id && row.operator_name }]"
                      :title="row.operator_id || !row.operator_name ? '' : `Operador del reporte: ${row.operator_name} (sin asignar)`"
                      @focus="openPicker(i, 'operator')"
                      @input="onPickerInput(i, row, 'operator')"
                      @blur="closePickerSoon"
                    />
                    <ul
                      v-if="picker.index === i && picker.kind === 'operator' && pickerSuggestions.length"
                      class="suggestions"
                    >
                      <li v-for="op in pickerSuggestions" :key="op.id" @mousedown.prevent="selectOperator(row, op)">
                        {{ op.name }}
                      </li>
                    </ul>
                  </td>
                  <td><input v-model="row.card" type="text" class="cell-input cell-input--sm" /></td>
                  <td><input v-model="row.ticket" type="text" class="cell-input cell-input--sm" /></td>
                  <td><input v-model="row.product" type="text" class="cell-input cell-input--sm" /></td>
                  <td><input v-model="row.odometer" type="number" step="0.01" min="0" class="cell-input cell-input--num" /></td>
                  <td><input v-model="row.liters" type="number" step="0.001" min="0" class="cell-input cell-input--num" /></td>
                  <td><input v-model="row.total_cost" type="number" step="0.01" min="0" class="cell-input cell-input--num" /></td>
                  <td>
                    <button
                      class="icon-btn icon-btn--danger"
                      title="Quitar esta fila"
                      @click="removeReportRow(i)"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="8" class="num"><strong>Totales</strong></td>
                  <td class="num"><strong>{{ formatNumber(reportTotals.liters) }}</strong></td>
                  <td class="num"><strong>{{ formatMoney(reportTotals.cost) }}</strong></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div class="actions-row">
            <button class="btn btn--primary" :disabled="report.saving" @click="importRefills">
              {{ report.saving ? 'Guardando…' : `Guardar ${report.rows.length} carga(s)` }}
            </button>
          </div>
        </template>
      </section>
    </div>

    <!-- ========================= Invoice modal ========================= -->
    <div v-if="invoiceModal.open" class="fuel-modal-backdrop" @click.self="closeInvoiceModal">
      <div class="fuel-modal" role="dialog" aria-modal="true">
        <div class="fuel-modal__head">
          <h3>Subir factura</h3>
          <button class="icon-btn" aria-label="Cerrar" @click="closeInvoiceModal">✕</button>
        </div>

        <div class="fuel-modal__body">
          <label class="field">
            <span>Proveedor</span>
            <select v-model="invoiceModal.providerId">
              <option :value="null" disabled>Selecciona un proveedor</option>
              <option v-for="p in providers" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </label>

          <label class="field">
            <span>Documento (PDF)</span>
            <input ref="invoiceFile" type="file" accept=".pdf" @change="onInvoiceFileChange" />
          </label>

          <div v-if="invoiceModal.parsing" class="muted">Analizando documento…</div>

          <div v-if="invoiceModal.warnings.length" class="warning-box">
            <p v-for="(w, i) in invoiceModal.warnings" :key="i">⚠ {{ w }}</p>
          </div>

          <div class="fields-grid">
            <label class="field">
              <span>No. de factura *</span>
              <input v-model="invoiceModal.fields.invoice_number" type="text" />
            </label>
            <label class="field">
              <span>Fecha de factura</span>
              <input v-model="invoiceModal.fields.invoice_date" type="date" />
            </label>
            <label class="field">
              <span>Inicio del periodo</span>
              <input v-model="invoiceModal.fields.period_start" type="date" />
            </label>
            <label class="field">
              <span>Fin del periodo</span>
              <input v-model="invoiceModal.fields.period_end" type="date" />
            </label>
            <label class="field">
              <span>Litros totales</span>
              <input v-model="invoiceModal.fields.total_liters" type="number" step="0.001" min="0" />
            </label>
            <label class="field">
              <span>Importe total ($)</span>
              <input v-model="invoiceModal.fields.total_amount" type="number" step="0.01" min="0" />
            </label>
          </div>
        </div>

        <div class="fuel-modal__foot">
          <button class="btn" @click="closeInvoiceModal">Cancelar</button>
          <button
            class="btn btn--primary"
            :disabled="!canSaveInvoice || invoiceModal.saving"
            @click="saveInvoice"
          >
            {{ invoiceModal.saving ? 'Guardando…' : 'Guardar factura' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'

const emptyInvoiceFields = () => ({
  invoice_number: '',
  invoice_date: '',
  period_start: '',
  period_end: '',
  total_liters: '',
  total_amount: '',
})

export default {
  name: 'FuelUploadDocuments',
  data() {
    return {
      section: 'invoices',
      providers: [],
      invoices: [],
      loadingInvoices: false,
      catalog: { units: [], operators: [] },
      // Which row/column currently shows its type-ahead suggestions.
      picker: { index: -1, kind: null },
      invoiceModal: {
        open: false,
        providerId: null,
        file: null,
        parsing: false,
        saving: false,
        warnings: [],
        fields: emptyInvoiceFields(),
      },
      report: {
        providerId: null,
        invoiceId: null,
        providerInvoices: [],
        file: null,
        parsing: false,
        saving: false,
        rows: [],
        warnings: [],
      },
    }
  },
  computed: {
    reportProvider() {
      return this.providers.find((p) => p.id === this.report.providerId) || null
    },
    pickerSuggestions() {
      const { index, kind } = this.picker
      const row = this.report.rows[index]
      if (!row || !kind) return []
      if (kind === 'unit') {
        const term = (row.unit_search || '').toLowerCase().trim()
        const units = term
          ? this.catalog.units.filter(
              (u) =>
                u.label.toLowerCase().includes(term) ||
                (u.plates || '').toLowerCase().includes(term)
            )
          : this.catalog.units
        return units.slice(0, 20)
      }
      const term = (row.operator_search || '').toLowerCase().trim()
      const operators = term
        ? this.catalog.operators.filter((op) => op.name.toLowerCase().includes(term))
        : this.catalog.operators
      return operators.slice(0, 20)
    },
    canSaveInvoice() {
      return (
        this.invoiceModal.providerId &&
        this.invoiceModal.file &&
        (this.invoiceModal.fields.invoice_number || '').trim()
      )
    },
    reportTotals() {
      let liters = 0
      let cost = 0
      for (const row of this.report.rows) {
        liters += parseFloat(row.liters) || 0
        cost += parseFloat(row.total_cost) || 0
      }
      return { liters: liters.toFixed(3), cost: cost.toFixed(2) }
    },
  },
  created() {
    this.fetchProviders()
    this.fetchInvoices()
    this.fetchCatalog()
  },
  methods: {
    authHeaders() {
      return { Authorization: 'Token ' + localStorage.getItem('authToken') }
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '—'
      const num = parseFloat(value)
      if (Number.isNaN(num)) return '—'
      return num.toLocaleString('es-MX', { maximumFractionDigits: 3 })
    },
    formatMoney(value) {
      if (value === null || value === undefined || value === '') return '—'
      const num = parseFloat(value)
      if (Number.isNaN(num)) return '—'
      return num.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
    },
    async fetchProviders() {
      try {
        const { data } = await axios.get(`${process.env.API_DIRECTORY}/fuel/providers`, {
          headers: this.authHeaders(),
        })
        this.providers = data.results || []
      } catch (error) {
        Swal.fire('Error', 'No se pudieron cargar los proveedores.', 'error')
      }
    },
    async fetchCatalog() {
      try {
        const { data } = await axios.get(`${process.env.API_DIRECTORY}/fuel/catalog`, {
          headers: this.authHeaders(),
        })
        this.catalog = { units: data.units || [], operators: data.operators || [] }
      } catch (error) {
        // Dropdowns degrade to "no asignado"; parsing/import still work.
        this.catalog = { units: [], operators: [] }
      }
    },
    async fetchInvoices() {
      this.loadingInvoices = true
      try {
        const { data } = await axios.get(`${process.env.API_DIRECTORY}/fuel/invoices`, {
          headers: this.authHeaders(),
        })
        this.invoices = data.results || []
      } catch (error) {
        Swal.fire('Error', 'No se pudieron cargar las facturas.', 'error')
      } finally {
        this.loadingInvoices = false
      }
    },

    // ----------------------------- Invoices -----------------------------
    openInvoiceModal() {
      this.invoiceModal = {
        open: true,
        providerId: null,
        file: null,
        parsing: false,
        saving: false,
        warnings: [],
        fields: emptyInvoiceFields(),
      }
    },
    closeInvoiceModal() {
      this.invoiceModal.open = false
    },
    onInvoiceFileChange(event) {
      this.invoiceModal.file = event.target.files[0] || null
      if (this.invoiceModal.file) this.parseInvoice()
    },
    async parseInvoice() {
      if (!this.invoiceModal.providerId) {
        Swal.fire('Falta el proveedor', 'Selecciona el proveedor antes de subir el documento.', 'warning')
        this.invoiceModal.file = null
        if (this.$refs.invoiceFile) this.$refs.invoiceFile.value = ''
        return
      }
      this.invoiceModal.parsing = true
      this.invoiceModal.warnings = []
      try {
        const formData = new FormData()
        formData.append('file', this.invoiceModal.file)
        formData.append('provider_id', this.invoiceModal.providerId)
        const { data } = await axios.post(
          `${process.env.API_DIRECTORY}/fuel/invoices/parse`,
          formData,
          { headers: this.authHeaders() }
        )
        const fields = data.fields || {}
        this.invoiceModal.fields = {
          invoice_number: fields.invoice_number || '',
          invoice_date: fields.invoice_date || '',
          period_start: fields.period_start || '',
          period_end: fields.period_end || '',
          total_liters: fields.total_liters || '',
          total_amount: fields.total_amount || '',
        }
        this.invoiceModal.warnings = data.warnings || []
      } catch (error) {
        const detail = error.response?.data?.error || 'No se pudo analizar el documento.'
        this.invoiceModal.warnings = [detail]
      } finally {
        this.invoiceModal.parsing = false
      }
    },
    async saveInvoice() {
      this.invoiceModal.saving = true
      try {
        const formData = new FormData()
        formData.append('file', this.invoiceModal.file)
        formData.append('provider_id', this.invoiceModal.providerId)
        for (const [key, value] of Object.entries(this.invoiceModal.fields)) {
          formData.append(key, value ?? '')
        }
        await axios.post(`${process.env.API_DIRECTORY}/fuel/invoices`, formData, {
          headers: this.authHeaders(),
        })
        this.closeInvoiceModal()
        Swal.fire('Guardada', 'La factura se registró correctamente.', 'success')
        this.fetchInvoices()
      } catch (error) {
        const detail = error.response?.data?.error || 'No se pudo guardar la factura.'
        Swal.fire('Error', detail, 'error')
      } finally {
        this.invoiceModal.saving = false
      }
    },

    // ----------------------------- Reports -----------------------------
    resetReportPreview() {
      this.report.rows = []
      this.report.warnings = []
      this.report.file = null
      this.report.invoiceId = null
      if (this.$refs.reportFile) this.$refs.reportFile.value = ''
      this.fetchProviderInvoices()
    },
    async fetchProviderInvoices() {
      this.report.providerInvoices = []
      if (!this.report.providerId) return
      try {
        const { data } = await axios.get(`${process.env.API_DIRECTORY}/fuel/invoices`, {
          headers: this.authHeaders(),
          params: { provider_id: this.report.providerId },
        })
        this.report.providerInvoices = data.results || []
      } catch (error) {
        this.report.providerInvoices = []
      }
    },
    onReportFileChange(event) {
      this.report.file = event.target.files[0] || null
      this.report.rows = []
      this.report.warnings = []
    },
    async parseReport() {
      this.report.parsing = true
      this.report.rows = []
      this.report.warnings = []
      try {
        const formData = new FormData()
        formData.append('file', this.report.file)
        formData.append('provider_id', this.report.providerId)
        const { data } = await axios.post(
          `${process.env.API_DIRECTORY}/fuel/reports/parse`,
          formData,
          { headers: this.authHeaders() }
        )
        // Normalize so every row is fully editable. The *_search fields feed
        // the type-ahead inputs: matched rows show the match, unmatched rows
        // show the report's raw value so the user sees what to look up.
        this.report.rows = (data.rows || []).map((row) => ({
          ...row,
          unit_id: row.unit_id ?? null,
          operator_id: row.operator_id ?? null,
          operator_name: row.operator_name || '',
          unit_search: row.unit_id
            ? row.unit_label
            : row.unit_eco || row.unit_plates || '',
          operator_search: row.operator_name || '',
        }))
        this.report.warnings = data.warnings || []
        if (!this.report.rows.length) {
          this.report.warnings.push('El reporte no contiene filas válidas.')
        }
      } catch (error) {
        const detail = error.response?.data?.error || 'No se pudo analizar el reporte.'
        Swal.fire('Error', detail, 'error')
      } finally {
        this.report.parsing = false
      }
    },
    removeReportRow(index) {
      this.report.rows.splice(index, 1)
      this.picker = { index: -1, kind: null }
    },
    openPicker(index, kind) {
      this.picker = { index, kind }
    },
    onPickerInput(index, row, kind) {
      // Typing invalidates the current assignment until a suggestion is picked.
      if (kind === 'unit') row.unit_id = null
      else row.operator_id = null
      this.picker = { index, kind }
    },
    closePickerSoon() {
      // Suggestions use mousedown.prevent, so selection fires before this runs;
      // the delay only covers stray blur orderings.
      setTimeout(() => {
        this.picker = { index: -1, kind: null }
      }, 150)
    },
    selectUnit(row, unit) {
      row.unit_id = unit.id
      row.unit_search = unit.label
      this.picker = { index: -1, kind: null }
    },
    selectOperator(row, operator) {
      row.operator_id = operator.id
      row.operator_search = operator.name
      this.picker = { index: -1, kind: null }
    },
    async importRefills() {
      this.report.saving = true
      try {
        const { data } = await axios.post(
          `${process.env.API_DIRECTORY}/fuel/refills/import`,
          {
            provider_id: this.report.providerId,
            invoice_id: this.report.invoiceId,
            rows: this.report.rows,
          },
          { headers: this.authHeaders() }
        )
        const linked = this.report.providerInvoices.find((inv) => inv.id === this.report.invoiceId)
        let message = `Se registraron ${data.created} carga(s) de combustible.`
        if (linked) {
          message += ` Ligadas a la factura ${linked.invoice_number}.`
        }
        if (data.errors?.length) {
          message += `\n\nFilas con problemas:\n${data.errors.join('\n')}`
        }
        Swal.fire('Cargas registradas', message, 'success')
        this.resetReportPreview()
      } catch (error) {
        const detail = error.response?.data?.error || 'No se pudieron guardar las cargas.'
        Swal.fire('Error', detail, 'error')
      } finally {
        this.report.saving = false
      }
    },
  },
}
</script>

<style scoped>
.fuel-upload {
  --bg: #f7f8fb;
  --muted: #667085;
  --text: #0f172a;
  --brand: #ffc434;
  --primary: #2563eb;
  --line: rgba(16, 24, 40, 0.12);
  --warn-bg: #fff7e6;
  --warn-border: #f5c76f;
  --warn-text: #8a5a00;
  min-height: 100vh;
  background: var(--bg);
  padding: 12vh 24px 48px;
}

.nav-bar-operations {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 10vh;
  background-color: var(--brand);
  z-index: 1000;
  display: flex;
  align-items: center;
  padding-left: 16px;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.05);
}
.logo-button {
  height: 55%;
  object-fit: contain;
  cursor: pointer;
}

.shell {
  max-width: 1100px;
  margin: 0 auto;
}

.header {
  margin-top: 12px;
}
.eyebrow {
  margin: 0;
  color: var(--muted);
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.header h1 {
  margin: 2px 0 0;
  font-size: 1.4rem;
  color: var(--text);
}
.sub {
  margin: 4px 0 0;
  color: var(--muted);
}

.tabs {
  display: flex;
  gap: 8px;
  margin-top: 18px;
  border-bottom: 1px solid var(--line);
}
.tab {
  border: none;
  background: transparent;
  padding: 10px 16px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--muted);
  cursor: pointer;
  border-bottom: 3px solid transparent;
}
.tab--active {
  color: var(--primary);
  border-bottom-color: var(--primary);
}

.panel {
  margin-top: 18px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
}
.panel__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
}
.panel__head h2 {
  margin: 0;
  font-size: 1.05rem;
  color: var(--text);
}
.muted {
  color: var(--muted);
  font-size: 0.9rem;
  margin: 4px 0 0;
}
.empty {
  padding: 36px 0;
  text-align: center;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}
.table th,
.table td {
  text-align: left;
  padding: 9px 10px;
  border-bottom: 1px solid var(--line);
  color: var(--text);
}
.table thead th {
  color: var(--muted);
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.table .num {
  text-align: right;
}

.table-scroll {
  overflow-x: auto;
}
/* While a type-ahead is open, reserve room inside the scroll container so the
   suggestions list is not clipped on the bottom rows. */
.table-scroll--picker-open {
  padding-bottom: 230px;
  margin-bottom: -230px;
}
.table--edit th,
.table--edit td {
  padding: 6px 4px;
  white-space: nowrap;
}

.cell-input {
  width: 100%;
  min-width: 70px;
  padding: 6px 7px;
  border: 1px solid var(--line);
  border-radius: 8px;
  font-size: 0.85rem;
  background: #fff;
  color: var(--text);
}
.cell-input--sm {
  min-width: 90px;
}
.cell-input--num {
  min-width: 90px;
  text-align: right;
}
.cell-input--date {
  min-width: 130px;
}
.cell-input--time {
  min-width: 100px;
}
.cell-input--picker {
  min-width: 160px;
  max-width: 220px;
}
.cell-input--warn {
  border-color: var(--warn-border);
  background: var(--warn-bg);
  color: var(--warn-text);
}

/* Type-ahead suggestions (same pattern as the client pickers in filters). */
.picker-cell {
  position: relative;
}
.suggestions {
  position: absolute;
  top: calc(100% - 2px);
  left: 4px;
  width: 280px;
  max-height: 220px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 8px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.16);
  list-style: none;
  margin: 0;
  padding: 4px 0;
  z-index: 30;
}
.suggestions li {
  padding: 6px 10px;
  font-size: 0.82rem;
  color: var(--text);
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.suggestions li:hover {
  background: #f2f6ff;
}

.icon-btn--danger {
  color: #b91c1c;
  font-size: 0.85rem;
  padding: 4px 6px;
  border-radius: 8px;
}
.icon-btn--danger:hover {
  background: rgba(185, 28, 28, 0.08);
}

.report-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 14px;
  margin-bottom: 14px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 0.85rem;
  color: var(--muted);
  min-width: 220px;
}
.field input,
.field select {
  padding: 9px 10px;
  border: 1px solid var(--line);
  border-radius: 10px;
  font-size: 0.92rem;
  background: #fff;
  color: var(--text);
}

.fields-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
}
.fields-grid .field {
  min-width: 0;
}

.warning-box {
  background: var(--warn-bg);
  border: 1px solid var(--warn-border);
  color: var(--warn-text);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 0.88rem;
  margin: 10px 0;
}
.warning-box p {
  margin: 2px 0;
}

.actions-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: #fff;
  color: var(--text);
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s ease;
}
.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.btn--primary {
  background: var(--primary);
  border-color: transparent;
  color: #fff;
}
.btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.3);
}

/* Modal. Custom class names on purpose: Bootstrap is loaded globally and its
   .modal / .modal-backdrop rules (display:none, opacity) would clash. */
.fuel-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: grid;
  place-items: center;
  /* Above the topbar (1000) but below SweetAlert2's container (1060) so
     alerts triggered from the modal stay readable. */
  z-index: 1050;
  padding: 20px;
}
.fuel-modal {
  background: #fff;
  border-radius: 16px;
  width: min(640px, 100%);
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.3);
}
.fuel-modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid var(--line);
}
.fuel-modal__head h3 {
  margin: 0;
  color: var(--text);
}
.icon-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  cursor: pointer;
  color: var(--muted);
}
.fuel-modal__body {
  padding: 16px 18px;
}
.fuel-modal__foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 18px;
  border-top: 1px solid var(--line);
}

@media (max-width: 720px) {
  .fields-grid {
    grid-template-columns: 1fr;
  }
  .panel__head {
    flex-direction: column;
  }
}

@media screen and (max-height: 586px) {
  .nav-bar-operations {
    height: 10vh;
  }
  .fuel-upload {
    padding-top: 12vh;
  }
}
</style>
